/* eslint-disable no-underscore-dangle */
import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { setRoom } from '@store/actionSlices/appConfig'
import { TokenPayloadInterface } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM, SortOrder } from '@store/constants'
import { RootStateType } from '@store/store'

import { GenericModalCard as ModalCard } from '@components/modals'

import {
  selectShowcaseRoomsFromResult,
  useGetShowcaseRoomsQuery,
} from '@api/showcase-room'

import InputHandler from '@utilities/form-util'
import { getQueryStringParams, hasToken } from '@utilities/helper'

interface RoomHandlerProps {
  room: string
  projectName: string
  token: TokenPayloadInterface
  canChangeRoom?: boolean
}

interface Option {
  label: string
  value: string
  disabled?: boolean
  hidden?: boolean
}

function RoomHandler({
  room,
  projectName,
  token,
  canChangeRoom = true,
}: RoomHandlerProps) {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const disable = React.useMemo(() => !hasToken(token), [token])
  const VISION_ROUTE = 'vision'

  const [toggled, setToggleModal] = React.useState(false)
  const [inputRoomValue, setInputRoomValue] = React.useState({
    value: room,
    isValid: true,
    message: '',
  })

  const { showcaseRooms, isLoaded: isShowcaseRoomsLoaded } =
    useGetShowcaseRoomsQuery(
      { projectName, pagination: false, sort_order: SortOrder.ASC },
      {
        skip: disable || !toggled,
        selectFromResult: selectShowcaseRoomsFromResult,
      }
    )

  const roomInputValidator = (value: string) => !!value

  const checkDefaultRoom = React.useCallback(
    (name: string) => name.toLocaleLowerCase() === DEFAULT_SHOWCASE_ROOM,
    []
  )

  const defaultRoom = React.useMemo(
    () => showcaseRooms.find((item) => checkDefaultRoom(item.name)),
    [showcaseRooms, checkDefaultRoom]
  )

  const isValidSelectedRoom = React.useMemo<boolean>(
    () =>
      showcaseRooms.some((item) => {
        if (checkDefaultRoom(item.name)) {
          return room === ''
        }
        return item.name === room
      }),
    [showcaseRooms, room, checkDefaultRoom]
  )

  const roomOptions = React.useMemo<Option[]>(() => {
    const options = showcaseRooms.map<Option>((item) => ({
      label: item.name,
      value: item.name,
    }))
    if (!isValidSelectedRoom) {
      options.unshift({
        label: 'Please select a room',
        value: '',
        hidden: true,
      })
    }
    return options
  }, [showcaseRooms, isValidSelectedRoom])

  const selectInputValue = React.useMemo(() => {
    if (inputRoomValue.value === '') {
      return defaultRoom?.name || ''
    }
    return inputRoomValue.value
  }, [inputRoomValue.value, defaultRoom])

  const handleSaveRoom = () => {
    if (inputRoomValue.value) {
      dispatch(setRoom(inputRoomValue.value))
      history.push(VISION_ROUTE)
      window.location.reload()
    }
  }

  React.useEffect(() => {
    setInputRoomValue({
      ...inputRoomValue,
      value: room,
    })
  }, [toggled])

  React.useEffect(() => {
    const { room: roomParam } = getQueryStringParams(location.search)
    if (roomParam !== undefined) {
      dispatch(setRoom(roomParam))
    }
  }, [])

  return (
    <>
      <ModalCard
        headerLabel="Room"
        okLabel="Save"
        toggle={(status: boolean) => setToggleModal(status)}
        okAction={() => handleSaveRoom()}
        isVisible={toggled}
        disableOk={
          !canChangeRoom ||
          inputRoomValue.value.length === 0 ||
          !inputRoomValue.isValid
        }
      >
        <div className="px-4 text-left">
          <b>Select showcase room:</b>
        </div>
        <div className="mx-4 my-3 flex items-center justify-center rounded border text-lg">
          {!isShowcaseRoomsLoaded ? (
            <div className="shimmer-container relative h-full w-full rounded-none opacity-50">
              <div className="flex h-full w-full items-center justify-between">
                <div className="h-4 w-40 rounded-full bg-zinc-300"></div>
              </div>
            </div>
          ) : (
            <select
              onChange={(event) =>
                InputHandler(
                  event,
                  setInputRoomValue,
                  'The given room is invalid',
                  roomInputValidator
                )
              }
              className={`outlline-none w-full cursor-pointer border-none focus:border focus:border-none ${
                !inputRoomValue.isValid && inputRoomValue.value
                  ? 'border-red-700'
                  : 'border-gray-300'
              }`}
              value={selectInputValue}
              disabled={!canChangeRoom}
            >
              {roomOptions.map((item) => (
                <option
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  hidden={item.hidden}
                >
                  {item.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="px-4 text-left text-sm">
          {!inputRoomValue.isValid && inputRoomValue.value && (
            <span className="ml-auto text-red-700">
              {inputRoomValue.message}
            </span>
          )}
        </div>
      </ModalCard>
      <button
        onClick={() => setToggleModal(!toggled)}
        type="button"
        className="text-blue-500 underline underline-offset-2"
      >
        Room
      </button>
    </>
  )
}

export default connect(
  ({ projectIdentity: { projectName }, token }: RootStateType) => ({
    projectName,
    token,
  })
)(RoomHandler)
